"use client"

import { Camera, Clock, History, LogOut, Save } from "lucide-react"
import { IDLE_LOGOUT_MS, IDLE_WARNING_MS } from "@/lib/session/config"

const WINDOW_S = Math.round((IDLE_LOGOUT_MS - IDLE_WARNING_MS) / 1000)

/** « Toujours là ? » : la derniere minute avant la deconnexion pour inactivite (CNX, R1) */
export function IdleWarningModal({
  secondsLeft,
  backgroundJob,
  editor,
  onStay,
  onSaveAndStay,
  onLogout,
}: {
  secondsLeft: number
  backgroundJob: boolean
  /** Le projet ouvert dans cet onglet, s'il y en a un */
  editor: { projectName: string; unsaved: boolean } | null
  onStay: () => void
  onSaveAndStay: () => void
  onLogout: () => void
}) {
  const unsaved = !!editor?.unsaved
  const m = Math.floor(secondsLeft / 60)
  const s = String(secondsLeft % 60).padStart(2, "0")
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4">
      <div role="alertdialog" aria-labelledby="idle-title" className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="p-6">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
            <Clock className="w-7 h-7" />
          </div>
          <h2 id="idle-title" className="text-lg font-bold text-slate-900">Toujours là ?</h2>
          <p className="text-sm text-slate-600 mt-1">Sans activité de votre part, vous serez déconnecté dans</p>
          <p className="font-mono text-4xl font-bold text-slate-900 mt-2 tabular-nums" aria-live="polite">{m}:{s}</p>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-2">
            <div className="h-full bg-amber-500 transition-[width] duration-1000 ease-linear" style={{ width: `${(secondsLeft / WINDOW_S) * 100}%` }} />
          </div>
          {editor && (
            <p className="text-sm text-slate-600 mt-3 flex gap-2">
              {unsaved ? (
                <>
                  <History className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Vos modifications non enregistrées sur « {editor.projectName} » seront gardées : elles vous seront proposées à votre prochaine connexion.</span>
                </>
              ) : (
                <span>Ce projet est enregistré : vous ne perdrez rien.</span>
              )}
            </p>
          )}
          {backgroundJob && (
            <p className="text-sm text-slate-600 mt-3 flex gap-2">
              <Camera className="w-4 h-4 mt-0.5 shrink-0" />
              Votre rendu ou votre vidéo en cours continue sur le serveur : vous le retrouverez dans le projet à votre retour.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-3.5 border-t border-slate-200 bg-slate-50">
          {unsaved ? (
            <button onClick={onSaveAndStay} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 border border-slate-200 bg-white hover:bg-slate-50">
              <Save className="w-4 h-4" />
              Enregistrer et rester
            </button>
          ) : (
            <button onClick={onLogout} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 border border-slate-200 bg-white hover:bg-slate-50">
              <LogOut className="w-4 h-4" />
              Se déconnecter
            </button>
          )}
          <button onClick={onStay} autoFocus className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700">
            Rester connecté
          </button>
        </div>
      </div>
    </div>
  )
}
